import { demoModeAtom, selectedDomainAtom, userAtom } from '@/global-store';
import { usePostOfferRequest } from '@/http/queries/usePostOfferRequest';
import { IconSend } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { type ReactElement, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Button,
  ComboboxLegacy,
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  InputLegacy,
  PhoneInput,
  Textarea,
  type ComboboxLegacyMultiProps,
} from '@eye-able/ui';
import { EMAIL_REGEX } from '@/helpers/constants';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

interface OfferContactFormValues {
  name: string;
  email: string;
  domain: string;
  message: string;
  selectedProducts: string[];
  phone: string;
}

export interface OfferContactDialogFormProps {
  product?: string | null;
  productFeature?: string;
  trigger: ReactElement;
}

function OfferContactDialogForm({
  product,
  productFeature,
  trigger,
}: OfferContactDialogFormProps) {
  const { t } = useInlineTranslation();
  const [user] = useAtom(userAtom);
  const [demoMode] = useAtom(demoModeAtom);
  const [selectedDomain] = useAtom(selectedDomainAtom);
  const [open, setOpen] = useState(false);
  const [dialogContentElement, setDialogContentElement] =
    useState<HTMLDivElement | null>(null);

  const sendRequest = usePostOfferRequest();

  const products = [
    'Eye-Able Report',
    'Eye-Able Audit',
    'Eye-Able Assist',
    t('governance'),
    'Eye-Able Ally',
    'Lighthouse',
    t('plainLanguage'),
    t('accessibilityStatementGenerator'),
    t('manualAccessibilityAuditProduct'),
    'Eye-Able Access',
  ].map((label) => ({ value: label, label }));

  const offer = [product, productFeature].filter(Boolean).join(' ');

  // The contact fields only render in demo mode, where a prospect fills them
  // in by hand; everywhere else they come from the signed-in user.
  const defaultValues: OfferContactFormValues = {
    message: `${t('featureContactFormMessage')} ${offer}`.trim(),
    domain: demoMode ? '' : (selectedDomain ?? ''),
    name: demoMode ? '' : (user?.fullName ?? ''),
    email: demoMode ? '' : (user?.email ?? ''),
    phone: demoMode ? '' : (user?.phone ?? ''),
    selectedProducts: product ? [product] : [],
  };

  const form = useForm<OfferContactFormValues>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const handleOpenChange = (nextOpen: boolean) => {
    form.reset(defaultValues);
    setOpen(nextOpen);
  };

  const handleSendRequest = async (values: OfferContactFormValues) => {
    const offerMessage = [
      'Category: Offer Request Form',
      `Domain: ${values.domain}`,
      `Current Page: ${document.title} | ${window.location.href}`,
      `Name: ${values.name}`,
      `Mail: ${values.email}`,
      `Phone: ${values.phone}`,
      `Message: ${values.message}`,
      `Product: ${values.selectedProducts.join(' & ')}`,
    ].join('\n');

    // Close the dialog before the mutation resolves so the success toast does
    // not overlap the still-open dialog.
    form.reset(defaultValues);
    setOpen(false);

    await sendRequest.mutateAsync(offerMessage);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        maxWidth='lg'
        className='eye:w-full'
        ref={setDialogContentElement}
      >
        <DialogTitle>
          {demoMode
            ? t('contactFormTitle')
            : `${t('offer')} - ${t('contactFormTitle')}`}
        </DialogTitle>
        <FormProvider {...form}>
          <form
            onSubmit={(event) =>
              void form.handleSubmit(handleSendRequest)(event)
            }
          >
            <div className='eye:flex eye:flex-col eye:gap-xs'>
              {demoMode ? (
                <>
                  <FormField
                    control={form.control}
                    name='name'
                    rules={{
                      required: t('fieldRequiredDynamic', {
                        fieldName: t('name'),
                      }),
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <InputLegacy
                            {...field}
                            autoComplete='name'
                            maxLength={254}
                            labelProps={{ label: t('name'), float: 'top' }}
                            error={!!fieldState.error}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='email'
                    rules={{
                      required: t('emailRequired'),
                      pattern: {
                        value: EMAIL_REGEX,
                        message: t('emailInvalid'),
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <InputLegacy
                            {...field}
                            autoComplete='email'
                            type='email'
                            maxLength={128}
                            labelProps={{ label: t('email'), float: 'top' }}
                            error={!!fieldState.error}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='phone'
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <PhoneInput
                          label={t('phone')}
                          ariaLabelNoCountry={t('noCountrySelected')}
                          popupContainer={dialogContentElement}
                          value={field.value}
                          onChange={field.onChange}
                          error={!!fieldState.error}
                          inputProps={{ name: field.name, maxLength: 20 }}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='domain'
                    rules={{
                      required: t('fieldRequiredDynamic', {
                        fieldName: t('domain'),
                      }),
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <InputLegacy
                            {...field}
                            maxLength={254}
                            labelProps={{ label: t('domain'), float: 'top' }}
                            error={!!fieldState.error}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              ) : null}

              <FormField
                control={form.control}
                name='selectedProducts'
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <ComboboxLegacy
                        error={!!fieldState.error}
                        fullWidth
                        items={products}
                        labelProps={{
                          label: t('wantedProducts'),
                          float: 'top',
                        }}
                        limitTags
                        multiSelect
                        value={field.value ?? []}
                        onSelect={
                          field.onChange as ComboboxLegacyMultiProps['onSelect']
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='message'
                rules={{
                  required: t('fieldRequiredDynamic', {
                    fieldName: t('message'),
                  }),
                }}
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        {...field}
                        label={t('message')}
                        rows={6}
                        required
                        aria-invalid={!!fieldState.error}
                        maxLength={5000}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogActions className='eye:mt-m'>
              <DialogClose asChild>
                <Button type='button' variant='outline'>
                  {t('cancel')}
                </Button>
              </DialogClose>
              <Button
                type='submit'
                variant='yellow'
                disabled={form.formState.isSubmitting}
                aria-busy={form.formState.isSubmitting}
                className='eye:gap-2xs'
              >
                <IconSend aria-hidden />
                {t('OfferContactFormSubmit')}
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}

export default OfferContactDialogForm;
